import _defineProperty from "/home/jenkins/workspace/tServices_Forms_Client_CICD_main/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/defineProperty";

/*
Copyright 2020 Splunk Inc.

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import './polyfill-safari10';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { ConsoleSpanExporter, SimpleSpanProcessor, BatchSpanProcessor, AlwaysOffSampler, AlwaysOnSampler, ParentBasedSampler } from '@opentelemetry/sdk-trace-base';
import { diag, DiagConsoleLogger, DiagLogLevel } from '@opentelemetry/api';
import { SplunkDocumentLoadInstrumentation } from './SplunkDocumentLoadInstrumentation';
import { SplunkXhrPlugin } from './SplunkXhrPlugin';
import { SplunkFetchInstrumentation } from './SplunkFetchInstrumentation';
import { SplunkUserInteractionInstrumentation, DEFAULT_AUTO_INSTRUMENTED_EVENTS, DEFAULT_AUTO_INSTRUMENTED_EVENT_NAMES } from './SplunkUserInteractionInstrumentation';
import { SplunkExporter } from './SplunkExporter';
import { ERROR_INSTRUMENTATION_NAME, SplunkErrorInstrumentation } from './SplunkErrorInstrumentation';
import { generateId, getPluginConfig } from './utils';
import { getRumSessionId, initSessionTracking } from './session';
import { SplunkWebSocketInstrumentation } from './SplunkWebSocketInstrumentation';
import { initWebVitals } from './webvitals';
import { SplunkLongTaskInstrumentation } from './SplunkLongTaskInstrumentation';
import { SplunkPageVisibilityInstrumentation } from './SplunkPageVisibilityInstrumentation';
import { SplunkConnectivityInstrumentation } from './SplunkConnectivityInstrumentation';
import { SplunkPostDocLoadResourceInstrumentation } from './SplunkPostDocLoadResourceInstrumentation';
import { SplunkWebTracerProvider } from './SplunkWebTracerProvider';
import { InternalEventTarget } from './EventTarget';
import { SplunkContextManager } from './SplunkContextManager';
import { Resource } from '@opentelemetry/resources';
import { SemanticResourceAttributes } from '@opentelemetry/semantic-conventions';
import { SDK_INFO, _globalThis } from '@opentelemetry/core';
import { VERSION } from './version';
import { getSyntheticsRunId, SYNTHETICS_RUN_ID_ATTRIBUTE } from './synthetics';
import { SplunkSpanAttributesProcessor } from './SplunkSpanAttributesProcessor';
import { SessionBasedSampler } from './SessionBasedSampler';
import { SplunkSocketIoClientInstrumentation } from './SplunkSocketIoClientInstrumentation';
export * from './SplunkExporter';
export * from './SplunkWebTracerProvider';
export * from './SessionBasedSampler';
var OPTIONS_DEFAULTS = {
  applicationName: 'unknown-browser-app',
  beaconEndpoint: undefined,
  bufferTimeout: 4000,
  bufferSize: 50,
  instrumentations: {},
  exporter: {
    factory: function factory(options) {
      return new SplunkExporter(options);
    }
  },
  spanProcessor: {
    factory: function factory(exporter, config) {
      return new BatchSpanProcessor(exporter, config);
    }
  },
  rumAccessToken: undefined
};

function migrateConfigOption(config, from, to) {
  if (from in config && !(to in config && config[to] !== OPTIONS_DEFAULTS[to])) {
    // @ts-expect-error There's no way to type this right
    config[to] = config[from];
  }
}
/**
 * Update configuration based on configuration option renames
 */


function migrateConfig(config) {
  migrateConfigOption(config, 'app', 'applicationName');
  migrateConfigOption(config, 'beaconUrl', 'beaconEndpoint');
  migrateConfigOption(config, 'environment', 'deploymentEnvironment');
  migrateConfigOption(config, 'rumAuth', 'rumAccessToken');
  return config;
}

var INSTRUMENTATIONS = [{
  Instrument: SplunkDocumentLoadInstrumentation,
  confKey: 'document',
  disable: false
}, {
  Instrument: SplunkXhrPlugin,
  confKey: 'xhr',
  disable: false
}, {
  Instrument: SplunkUserInteractionInstrumentation,
  confKey: 'interactions',
  disable: false
}, {
  Instrument: SplunkPostDocLoadResourceInstrumentation,
  confKey: 'postload',
  disable: false
}, {
  Instrument: SplunkFetchInstrumentation,
  confKey: 'fetch',
  disable: false
}, {
  Instrument: SplunkWebSocketInstrumentation,
  confKey: 'websocket',
  disable: true
}, {
  Instrument: SplunkLongTaskInstrumentation,
  confKey: 'longtask',
  disable: false
}, {
  Instrument: SplunkErrorInstrumentation,
  confKey: ERROR_INSTRUMENTATION_NAME,
  disable: false
}, {
  Instrument: SplunkPageVisibilityInstrumentation,
  confKey: 'visibility',
  disable: true
}, {
  Instrument: SplunkConnectivityInstrumentation,
  confKey: 'connectivity',
  disable: true
}, {
  Instrument: SplunkSocketIoClientInstrumentation,
  confKey: 'socketio',
  disable: true
}];
export var INSTRUMENTATIONS_ALL_DISABLED = INSTRUMENTATIONS.map(function (instrumentation) {
  return instrumentation.confKey;
}).reduce(function (acc, key) {
  acc[key] = false;
  return acc;
}, {
  'webvitals': false
});

function buildExporter(options) {
  var beaconUrl = options.beaconEndpoint + (options.rumAccessToken ? '?auth=' + options.rumAccessToken : '');
  return options.exporter.factory({
    beaconUrl: beaconUrl,
    onAttributesSerializing: options.exporter.onAttributesSerializing
  });
}

var inited = false;

var _deregisterInstrumentations;

var _deinitSessionTracking;

var _errorInstrumentation;

var _postDocLoadInstrumentation;

var eventTarget;
export var SplunkRum = {
  DEFAULT_AUTO_INSTRUMENTED_EVENTS: DEFAULT_AUTO_INSTRUMENTED_EVENTS,
  DEFAULT_AUTO_INSTRUMENTED_EVENT_NAMES: DEFAULT_AUTO_INSTRUMENTED_EVENT_NAMES,
  // Re-export samplers as properties for easier use in CDN build
  AlwaysOnSampler: AlwaysOnSampler,
  AlwaysOffSampler: AlwaysOffSampler,
  ParentBasedSampler: ParentBasedSampler,
  SessionBasedSampler: SessionBasedSampler,

  get inited() {
    return inited;
  },

  _internalInit: function _internalInit(options) {
    SplunkRum.init(Object.assign(Object.assign({}, OPTIONS_DEFAULTS), options));
  },
  init: function init(options) {
    var _Object$assign,
        _this = this;

    // "env" based config still a bad idea for web
    if (!('OTEL_TRACES_EXPORTER' in _globalThis)) {
      _globalThis.OTEL_TRACES_EXPORTER = 'none';
    }

    diag.setLogger(new DiagConsoleLogger(), (options === null || options === void 0 ? void 0 : options.debug) ? DiagLogLevel.DEBUG : DiagLogLevel.WARN);

    if (typeof window !== 'object') {
      diag.error('SplunkRum: Non-browser environment detected, aborting');
      return;
    }

    if (typeof Symbol !== 'function') {
      diag.error('SplunkRum: browser not supported, disabling instrumentation.');
      return;
    }

    eventTarget = new InternalEventTarget();
    var processedOptions = Object.assign({}, OPTIONS_DEFAULTS, migrateConfig(options), {
      exporter: Object.assign({}, OPTIONS_DEFAULTS.exporter, options.exporter)
    });

    if (inited) {
      diag.warn('SplunkRum already init()ed.');
      return;
    }

    if (processedOptions.realm) {
      if (!processedOptions.beaconEndpoint) {
        processedOptions.beaconEndpoint = "https://rum-ingest.".concat(processedOptions.realm, ".signalfx.com/v1/rum");
      } else {
        diag.warn('SplunkRum: Realm value ignored (beaconEndpoint has been specified)');
      }
    }

    if (!processedOptions.debug) {
      if (!processedOptions.beaconEndpoint) {
        throw new Error('SplunkRum.init( {beaconEndpoint: \'https://something\'} ) is required.');
      } else if (!processedOptions.beaconEndpoint.startsWith('https') && !processedOptions.allowInsecureBeacon) {
        throw new Error('Not using https is unsafe, if you want to force it use allowInsecureBeacon option.');
      }

      if (!processedOptions.rumAccessToken) {
        diag.warn('rumAccessToken will be required in the future');
      }
    }

    var instanceId = generateId(64);
    _deinitSessionTracking = initSessionTracking(instanceId, eventTarget, processedOptions.cookieDomain).deinit;
    var ignoreUrls = processedOptions.ignoreUrls,
        applicationName = processedOptions.applicationName,
        deploymentEnvironment = processedOptions.deploymentEnvironment,
        version = processedOptions.version; // enabled: false prevents registerInstrumentations from enabling instrumentations in constructor
    // they will be enabled in registerInstrumentations

    var pluginDefaults = {
      ignoreUrls: ignoreUrls,
      enabled: false
    };
    var resourceAttrs = Object.assign(Object.assign({}, SDK_INFO), (_Object$assign = {}, _defineProperty(_Object$assign, SemanticResourceAttributes.TELEMETRY_SDK_NAME, '@splunk/otel-web'), _defineProperty(_Object$assign, SemanticResourceAttributes.TELEMETRY_SDK_VERSION, VERSION), _defineProperty(_Object$assign, 'splunk.rumVersion', VERSION), _defineProperty(_Object$assign, 'splunk.scriptInstance', instanceId), _defineProperty(_Object$assign, 'app', applicationName), _Object$assign));
    var syntheticsRunId = getSyntheticsRunId();

    if (syntheticsRunId) {
      resourceAttrs[SYNTHETICS_RUN_ID_ATTRIBUTE] = syntheticsRunId;
    }

    var provider = new SplunkWebTracerProvider(Object.assign(Object.assign({}, processedOptions.tracer), {
      resource: new Resource(resourceAttrs)
    }));
    Object.defineProperty(provider.resource.attributes, 'splunk.rumSessionId', {
      get: function get() {
        return getRumSessionId();
      },
      configurable: true,
      enumerable: true
    });
    var instrumentations = INSTRUMENTATIONS.map(function (_ref) {
      var Instrument = _ref.Instrument,
          confKey = _ref.confKey,
          disable = _ref.disable;
      var pluginConf = getPluginConfig(processedOptions.instrumentations[confKey], pluginDefaults, disable);

      if (pluginConf) {
        // @ts-expect-error Can't mark in any way that processedOptions.instrumentations[confKey] is of specifc config type
        var instrumentation = new Instrument(pluginConf);

        if (confKey === ERROR_INSTRUMENTATION_NAME && instrumentation instanceof SplunkErrorInstrumentation) {
          _errorInstrumentation = instrumentation;
        }

        if (confKey === 'postload' && instrumentation instanceof SplunkPostDocLoadResourceInstrumentation) {
          _postDocLoadInstrumentation = instrumentation;
        }

        return instrumentation;
      }

      return null;
    }).filter(function (a) {
      return Boolean(a);
    });
    this.attributesProcessor = new SplunkSpanAttributesProcessor(Object.assign(Object.assign(Object.assign({}, deploymentEnvironment ? {
      environment: deploymentEnvironment,
      'deployment.environment': deploymentEnvironment
    } : {}), version ? {
      'app.version': version
    } : {}), processedOptions.globalAttributes || {}));
    provider.addSpanProcessor(this.attributesProcessor);

    if (processedOptions.beaconEndpoint) {
      var exporter = buildExporter(processedOptions);
      var spanProcessor = processedOptions.spanProcessor.factory(exporter, {
        scheduledDelayMillis: processedOptions.bufferTimeout,
        maxExportBatchSize: processedOptions.bufferSize
      });
      provider.addSpanProcessor(spanProcessor);
      this._processor = spanProcessor;
    }

    if (processedOptions.debug) {
      provider.addSpanProcessor(new SimpleSpanProcessor(new ConsoleSpanExporter()));
    }

    window.addEventListener('visibilitychange', function () {
      // this condition applies when the page is hidden or when it's closed
      // see for more details: https://developers.google.com/web/updates/2018/07/page-lifecycle-api#developer-recommendations-for-each-state
      if (document.visibilityState === 'hidden') {
        _this._processor.forceFlush();
      }
    });
    provider.register({
      contextManager: new SplunkContextManager(Object.assign(Object.assign({}, processedOptions.context), {
        onBeforeContextStart: function onBeforeContextStart() {
          return _postDocLoadInstrumentation === null || _postDocLoadInstrumentation === void 0 ? void 0 : _postDocLoadInstrumentation.onBeforeContextChange();
        },
        onBeforeContextEnd: function onBeforeContextEnd() {
          return _postDocLoadInstrumentation === null || _postDocLoadInstrumentation === void 0 ? void 0 : _postDocLoadInstrumentation.onBeforeContextChange();
        }
      }))
    }); // After context manager registration so instrumentation event listeners are affected accordingly

    _deregisterInstrumentations = registerInstrumentations({
      tracerProvider: provider,
      instrumentations: instrumentations
    });
    this.provider = provider;
    var vitalsConf = getPluginConfig(processedOptions.instrumentations.webvitals);

    if (vitalsConf !== false) {
      initWebVitals(provider);
    }

    inited = true;
    diag.info('SplunkRum.init() complete');
  },
  deinit: function deinit() {
    if (!inited) {
      return;
    }

    _deregisterInstrumentations === null || _deregisterInstrumentations === void 0 ? void 0 : _deregisterInstrumentations();
    _deregisterInstrumentations = undefined;
    _deinitSessionTracking === null || _deinitSessionTracking === void 0 ? void 0 : _deinitSessionTracking();
    _deinitSessionTracking = undefined;
    this.provider.shutdown();
    delete this.provider;
    eventTarget = undefined;
    diag.disable();
    inited = false;
  },
  setGlobalAttributes: function setGlobalAttributes(attributes) {
    var _a, _b;

    (_a = this.attributesProcessor) === null || _a === void 0 ? void 0 : _a.setGlobalAttributes(attributes);
    eventTarget === null || eventTarget === void 0 ? void 0 : eventTarget.emit('global-attributes-changed', {
      attributes: ((_b = this.attributesProcessor) === null || _b === void 0 ? void 0 : _b.getGlobalAttributes()) || {}
    });
  },
  getGlobalAttributes: function getGlobalAttributes() {
    var _a;

    return ((_a = this.attributesProcessor) === null || _a === void 0 ? void 0 : _a.getGlobalAttributes()) || {};
  },
  _experimental_getGlobalAttributes: function _experimental_getGlobalAttributes() {
    return this.getGlobalAttributes();
  },
  error: function error() {
    if (!inited) {
      diag.debug('SplunkRum not inited');
      return;
    }

    if (!_errorInstrumentation) {
      diag.error('Error was reported, but error instrumentation is disabled.');
      return;
    }

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _errorInstrumentation.report('SplunkRum.error', args);
  },
  addEventListener: function addEventListener(name, callback) {
    eventTarget === null || eventTarget === void 0 ? void 0 : eventTarget.addEventListener(name, callback);
  },
  removeEventListener: function removeEventListener(name, callback) {
    eventTarget === null || eventTarget === void 0 ? void 0 : eventTarget.removeEventListener(name, callback);
  },
  _experimental_addEventListener: function _experimental_addEventListener(name, callback) {
    return this.addEventListener(name, callback);
  },
  _experimental_removeEventListener: function _experimental_removeEventListener(name, callback) {
    return this.removeEventListener(name, callback);
  },
  getSessionId: function getSessionId() {
    return getRumSessionId();
  },
  _experimental_getSessionId: function _experimental_getSessionId() {
    return this.getSessionId();
  }
};
export default SplunkRum;